import { PayloadAction } from '@reduxjs/toolkit';
import { EntityTypes } from 'constants/entities';
import { IKeyArray } from 'types/IKeyArray';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { preview } from './register';
import { CertificateStatus } from 'types/domain/certificate';
import { flowActions } from './flow';

export interface IPreviewState {
  modules: IKeyArray<IPreviewModule>;
  dialogues: IKeyArray<IPreviewDialogue>;
  verificationRequests: IKeyArray<IPreviewVerification>;
  certificates: IKeyArray<IPreviewCertificate>;
  variables: IKeyArray<IPreviewVariable>;
  groups: IKeyArray<IPreviewGroup>;
  type: PreviewType;
}

export enum PreviewType {
  COURSE = 0,
  MODULE = 1,
}

export interface IPreviewModule {
  id: number;
  sessionAttemptId?: number;
  /**
   * The action a module preview was opened on, and the attempt it was opened in.
   * The synthetic history (the path leading up to it) is anchored here, not on
   * the current action, so it stays put as the learner moves around the preview.
   */
  startActionId?: number;
  startSessionAttemptId?: number;
  modifiedDate: number;
}

export interface IPreviewCertificate {
  id: number;
  modifiedDate: number;
  status: CertificateStatus;
}

export interface IPreviewDialogue {
  id: number;
  modifiedDate: number;
}

export interface IPreviewVerification {
  id: number;
  modifiedDate: number;
}

export interface IPreviewVariable {
  id: number;
  pass?: boolean;
  value?: number;
  variableOptionId?: number;
  modifiedDate: number;
}

export interface IPreviewGroup {
  id: number;
  enabled: boolean;
}

export interface IPreviewUpdate {
  type: EntityTypes;
  id: number;
  data?: any;
}

const initialState: IPreviewState = {
  modules: {},
  verificationRequests: {},
  variables: {},
  groups: {},
  certificates: {},
  dialogues: {},
  type: null,
};

export interface IJump {
  index?: number;
  actionId: number;
}

const slice = createSlice({
  name: 'preview',
  initialState,
  reducers: {
    updatePreview: (state, action: PayloadAction<IPreviewUpdate>) => {
      switch (action.payload.type) {
        case EntityTypes.MODULE:
          state.modules[action.payload.id] = {
            ...state.modules[action.payload.id],
            id: action.payload.id,
            ...(action.payload.data || {}),
            modifiedDate: new Date().getTime(),
          };
          break;
        case EntityTypes.COURSE_NODE:
          state.dialogues[action.payload.id] = {
            ...state.dialogues[action.payload.id],
            id: action.payload.id,
            ...(action.payload.data || {}),
            modifiedDate: new Date().getTime(),
          };
          break;
        case EntityTypes.VERIFICATION_REQUEST:
          state.verificationRequests[action.payload.id] = {
            ...state.verificationRequests[action.payload.id],
            id: action.payload.id,
            ...(action.payload.data || {}),
            modifiedDate: new Date().getTime(),
          };
          break;
        case EntityTypes.VARIABLE:
          state.variables[action.payload.id] = {
            ...state.variables[action.payload.id],
            id: action.payload.id,
            ...(action.payload.data || {}),
            modifiedDate: new Date().getTime(),
          };
          break;
        case EntityTypes.GROUP:
          state.groups[action.payload.id] = {
            ...state.groups[action.payload.id],
            id: action.payload.id,
            ...(action.payload.data || {}),
          };
          break;
        case EntityTypes.CERTIFICATE:
          state.certificates[action.payload.id] = {
            ...state.certificates[action.payload.id],
            id: action.payload.id,
            ...(action.payload.data || {}),
          };
          break;
      }
    },
    clearPreview(state) {
      state = initialState;
    },
    jumpToAction(state, action: PayloadAction<IJump>) {},
  },
  extraReducers: builder => {
    builder.addCase(preview, (state, action) => {
      return { ...initialState, type: action.payload.moduleId ? PreviewType.MODULE : PreviewType.COURSE };
    });
    builder.addCase(flowActions.updateFlowPage, (state, action) => {
      return initialState;
    });
  },
});

export const {
  actions: { updatePreview, clearPreview, jumpToAction },
  reducer,
} = slice;

export default reducer;
